import { RELAY_SOLANA_MAINNET_CHAIN_ID } from "../../anyspend/constants/index.js";
import { ABSTRACT_PUBLIC_RPC, ARBITRUM_PUBLIC_RPC, AVALANCHE_PUBLIC_RPC, BASE_PUBLIC_RPC, BSC_PUBLIC_RPC, ETHEREUM_PUBLIC_RPC, HYPEREVM_PUBLIC_RPC, OPTIMISM_PUBLIC_RPC, PLUME_PUBLIC_RPC, POLYGON_PUBLIC_RPC, } from "../../anyspend/constants/rpc.js";
import { b3Viem } from "../../shared/constants/chains/b3Viem.js";
import invariant from "invariant";
import { createPublicClient, createWalletClient, defineChain, http, parseEther, } from "viem";
import { abstract, arbitrum, avalanche, base, bsc, mainnet, optimism, polygon } from "viem/chains";
import { ChainType } from "../types/chain.js";
import { getAvaxToken, getBnbToken, getEthToken, getHyperEVMNativeToken, getHyperliquidUSDCToken, getPlumeNativeToken, getPolToken, getSolanaToken, HYPEREVM_CHAIN_ID, HYPERLIQUID_CHAIN_ID, PLUME_CHAIN_ID, } from "./token.js";
function getCustomEvmChain(chain, rpcUrl) {
    return defineChain({ ...chain, rpcUrls: { default: { http: [rpcUrl] } } });
}
/**
 * Global RPC URL overrides for EVM chains.
 * Use setChainRpcOverrides() to configure custom RPC endpoints.
 */
let chainRpcOverrides = {};
/**
 * Set custom RPC URL overrides for specific chains.
 * These overrides are used by chainIdToPublicClient and chainIdToWalletClient.
 *
 * @param overrides - A record mapping chain IDs to custom RPC URLs
 *
 * @example
 * // Set once at app initialization using environment variables
 * setChainRpcOverrides({
 *   1: process.env.ETHEREUM_RPC_URL,
 *   8453: process.env.BASE_RPC_URL,
 *   42161: process.env.ARBITRUM_RPC_URL,
 * });
 */
export function setChainRpcOverrides(overrides) {
    chainRpcOverrides = { ...overrides };
}
/**
 * Get the current RPC URL overrides.
 */
export function getChainRpcOverrides() {
    return { ...chainRpcOverrides };
}
/**
 * Clear all RPC URL overrides.
 */
export function clearChainRpcOverrides() {
    chainRpcOverrides = {};
}
export const hyperEVM = defineChain({
    id: HYPEREVM_CHAIN_ID,
    name: "HyperEVM",
    nativeCurrency: { name: "Hyperliquid", symbol: "HYPE", decimals: 18 },
    rpcUrls: { default: { http: [HYPEREVM_PUBLIC_RPC] } },
    blockExplorers: { default: { name: "HyperEVM Explorer", url: "https://hyperevmscan.io/" } },
});
export const plume = defineChain({
    id: PLUME_CHAIN_ID,
    name: "Plume",
    nativeCurrency: { name: "Plume", symbol: "PLUME", decimals: 18 },
    rpcUrls: { default: { http: [PLUME_PUBLIC_RPC] } },
    blockExplorers: { default: { name: "Plume Explorer", url: "https://explorer.plume.org" } },
});
// export const b4testnet = defineChain({
//   id: 19934,
//   name: "B4 Testnet",
//   network: "b4-testnet",
//   nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
//   rpcUrls: { default: { http: ["https://19934.rpc.thirdweb.com/c2dce731fe7fa39edc1d0ea5a6a5e11e"] } },
// });
export const EVM_MAINNET = {
    [mainnet.id]: {
        id: mainnet.id,
        name: mainnet.name,
        type: ChainType.EVM,
        logoUrl: "https://assets.relay.link/icons/square/1/light.png",
        nativeRequired: parseEther("0.001"),
        canDepositNative: true,
        defaultToken: getEthToken(mainnet.id),
        nativeToken: getEthToken(mainnet.id),
        viem: getCustomEvmChain(mainnet, ETHEREUM_PUBLIC_RPC),
        pollingInterval: 4000, // 4 seconds for Ethereum mainnet
        zapperEnum: "ETHEREUM_MAINNET",
        coingeckoName: "eth",
        wethAddress: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2",
    },
    [arbitrum.id]: {
        id: arbitrum.id,
        name: "Arbitrum",
        type: ChainType.EVM,
        logoUrl: "https://assets.relay.link/icons/square/42161/light.png",
        nativeRequired: parseEther("0.0001"),
        canDepositNative: true,
        defaultToken: getEthToken(arbitrum.id),
        nativeToken: getEthToken(arbitrum.id),
        viem: getCustomEvmChain(arbitrum, ARBITRUM_PUBLIC_RPC),
        pollingInterval: 500, // 500ms for Arbitrum's fast blocks
        zapperEnum: "ARBITRUM_MAINNET",
        coingeckoName: "arbitrum",
        wethAddress: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1",
    },
    [base.id]: {
        id: base.id,
        name: base.name,
        logoUrl: "https://assets.relay.link/icons/square/8453/light.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.0001"),
        canDepositNative: true,
        defaultToken: getEthToken(base.id),
        nativeToken: getEthToken(base.id),
        viem: getCustomEvmChain(base, BASE_PUBLIC_RPC),
        pollingInterval: 1000, // 1 second for Base
        zapperEnum: "BASE_MAINNET",
        coingeckoName: "base",
        wethAddress: "0x4200000000000000000000000000000000000006",
    },
    [optimism.id]: {
        id: optimism.id,
        name: optimism.name,
        logoUrl: "https://assets.relay.link/icons/square/10/light.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.0001"),
        canDepositNative: true,
        defaultToken: getEthToken(optimism.id),
        nativeToken: getEthToken(optimism.id),
        viem: getCustomEvmChain(optimism, OPTIMISM_PUBLIC_RPC),
        pollingInterval: 1000, // 1 second for Optimism
        zapperEnum: "OPTIMISM_MAINNET",
        coingeckoName: "optimism",
        wethAddress: "0x4200000000000000000000000000000000000006",
    },
    [polygon.id]: {
        id: polygon.id,
        name: polygon.name,
        logoUrl: "https://assets.relay.link/icons/square/137/light.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.5"),
        canDepositNative: true,
        defaultToken: getPolToken(),
        nativeToken: getPolToken(),
        viem: getCustomEvmChain(polygon, POLYGON_PUBLIC_RPC),
        pollingInterval: 1000, // 1 second for Polygon
        zapperEnum: "POLYGON_MAINNET",
        coingeckoName: "polygon_pos",
        wethAddress: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270",
    },
    [avalanche.id]: {
        id: avalanche.id,
        name: avalanche.name,
        logoUrl: "https://assets.relay.link/icons/square/43114/light.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.01"),
        canDepositNative: true,
        defaultToken: getAvaxToken(),
        nativeToken: getAvaxToken(),
        viem: getCustomEvmChain(avalanche, AVALANCHE_PUBLIC_RPC),
        pollingInterval: 1000, // 1 second for Avalanche
        zapperEnum: "AVALANCHE_MAINNET",
        coingeckoName: "avax",
        wethAddress: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7",
    },
    [bsc.id]: {
        id: bsc.id,
        name: bsc.name,
        logoUrl: "https://avatars.githubusercontent.com/u/45615063?s=280&v=4",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.0001"),
        canDepositNative: true,
        defaultToken: getBnbToken(),
        nativeToken: getBnbToken(),
        viem: getCustomEvmChain(bsc, BSC_PUBLIC_RPC),
        pollingInterval: 1000, // 1 second for BSC
        zapperEnum: "BSC_MAINNET",
        coingeckoName: "bsc",
        wethAddress: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c",
    },
    [b3Viem.id]: {
        id: b3Viem.id,
        name: b3Viem.name,
        logoUrl: "https://assets.relay.link/icons/square/8333/light.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.0001"),
        canDepositNative: true,
        defaultToken: getEthToken(b3Viem.id),
        nativeToken: getEthToken(b3Viem.id),
        viem: b3Viem,
        pollingInterval: 1000, // 1 second for B3
        zapperEnum: "B3_MAINNET",
        coingeckoName: "b3",
        wethAddress: "0x4200000000000000000000000000000000000006",
    },
    [abstract.id]: {
        id: abstract.id,
        name: abstract.name,
        logoUrl: "https://assets.relay.link/icons/square/2741/light.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.0002"),
        canDepositNative: true,
        defaultToken: getEthToken(abstract.id),
        nativeToken: getEthToken(abstract.id),
        viem: getCustomEvmChain(abstract, ABSTRACT_PUBLIC_RPC),
        pollingInterval: 3000, // 3 seconds for Abstract
        zapperEnum: "ABSTRACT_MAINNET",
        coingeckoName: "abstract",
        wethAddress: "0x3439153eb7af838ad19d56e1571fbd09333c2809",
    },
    [hyperEVM.id]: {
        id: hyperEVM.id,
        name: hyperEVM.name,
        logoUrl: "https://s2.coinmarketcap.com/static/img/coins/64x64/32196.png",
        type: ChainType.EVM,
        nativeRequired: parseEther("0.01"),
        canDepositNative: true,
        defaultToken: getHyperEVMNativeToken(),
        nativeToken: getHyperEVMNativeToken(),
        viem: hyperEVM,
        pollingInterval: 1000, // 1 second for Hyperliquid
        coingeckoName: "hyperevm",
        wethAddress: "0x5555555555555555555555555555555555555555",
    },
    [plume.id]: {
        id: plume.id,
        name: plume.name,
        logoUrl: "https://cdn.b3.fun/b3os-icons/plume.svg",
        type: ChainType.EVM,
        nativeRequired: parseEther("5"), // 5 PLUME ≈ $0.06 — gas reserve; Plume gasPrice ~1000 gwei × 2.2M gas (approve+deposit) needs ~2.2 PLUME, +headroom for spikes
        canDepositNative: true,
        defaultToken: getPlumeNativeToken(),
        nativeToken: getPlumeNativeToken(),
        viem: plume,
        pollingInterval: 1000, // 1 second for Plume (L2 fast blocks)
        coingeckoName: "plume-network",
        wethAddress: "0xea237441c92cae6fc17caaf9a7acb3f953be4bd1", // WPLUME (wrapped native)
    },
};
export const EVM_TESTNET = {
// [sepolia.id]: {
//   id: sepolia.id,
//   name: sepolia.name,
//   logoUrl: "https://assets.relay.link/icons/square/1/light.png",
//   type: ChainType.EVM,
//   nativeRequired: parseEther("0.00001"),
//   canDepositNative: true,
//   defaultToken: getEthToken(sepolia.id),
//   nativeToken: getEthToken(sepolia.id),
//   viem: sepolia,
//   pollingInterval: 1000, // 1 second for Sepolia
//   coingeckoName: "sepolia-testnet",
// },
// [baseSepolia.id]: {
//   id: baseSepolia.id,
//   name: baseSepolia.name,
//   logoUrl: "https://assets.relay.link/icons/square/8453/light.png",
//   type: ChainType.EVM,
//   nativeRequired: parseEther("0.00001"),
//   canDepositNative: true,
//   defaultToken: getEthToken(baseSepolia.id),
//   nativeToken: getEthToken(baseSepolia.id),
//   viem: baseSepolia,
//   pollingInterval: 1000, // 1 second for Base Sepolia
//   coingeckoName: null,
// },
// [b3Sepolia.id]: {
//   id: b3Sepolia.id,
//   name: b3Sepolia.name,
//   logoUrl: "https://assets.relay.link/icons/square/8333/light.png",
//   type: ChainType.EVM,
//   nativeRequired: parseEther("0.00001"),
//   canDepositNative: true,
//   defaultToken: getEthToken(b3Sepolia.id),
//   nativeToken: getEthToken(b3Sepolia.id),
//   viem: b3Sepolia,
//   pollingInterval: 1000, // 1 second for B3 Sepolia
//   coingeckoName: null,
// },
// [b4testnet.id]: {
//   id: b4testnet.id,
//   logoUrl: "https://cdn.b3.fun/b4-logo.png",
//   type: ChainType.EVM,
//   viem: b4testnet,
//   requireNativeBalance: parseEther("0.00001"),
//   supportDepositNative: true,
// },
};
export const SOLANA_MAINNET = {
    id: RELAY_SOLANA_MAINNET_CHAIN_ID,
    name: "Solana",
    logoUrl: "https://assets.relay.link/icons/square/792703809/light.png",
    type: ChainType.SOLANA,
    nativeRequired: BigInt(10000000), // 0.01 SOL
    canDepositNative: true,
    defaultToken: getSolanaToken(),
    nativeToken: getSolanaToken(),
    coingeckoName: "solana",
};
export const HYPERLIQUID_MAINNET = {
    id: HYPERLIQUID_CHAIN_ID,
    name: "Hyperliquid",
    type: ChainType.HYPERLIQUID,
    logoUrl: "https://s2.coinmarketcap.com/static/img/coins/64x64/32196.png",
    nativeRequired: BigInt(0), // No native transfer needed - using Relay's useDepositAddress flow (USDC is native)
    canDepositNative: true, // Can deposit USDC (native token)
    defaultToken: getHyperliquidUSDCToken(),
    nativeToken: getHyperliquidUSDCToken(),
    coingeckoName: null,
    apiUrl: "https://api.hyperliquid.xyz",
    blockExplorer: {
        url: "https://app.hyperliquid.xyz/explorer",
    },
};
export const EVM_CHAINS = { ...EVM_MAINNET, ...EVM_TESTNET };
export const SOLANA_CHAINS = { [RELAY_SOLANA_MAINNET_CHAIN_ID]: SOLANA_MAINNET };
export const HYPERLIQUID_CHAINS = {
    [HYPERLIQUID_CHAIN_ID]: HYPERLIQUID_MAINNET,
};
export const ALL_CHAINS = { ...EVM_CHAINS, ...SOLANA_CHAINS, ...HYPERLIQUID_CHAINS };
export function getSolanaChains(network) {
    invariant(network === "mainnet", "Solana chain is only supported on mainnet");
    return SOLANA_MAINNET;
}
export function getAllEvmChains(network) {
    return network === "mainnet" ? EVM_MAINNET : EVM_TESTNET;
}
export function getChainType(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} is not supported`);
    return ALL_CHAINS[chainId].type;
}
export function chainIdToPublicClient(chainId) {
    invariant(EVM_CHAINS[chainId], `Chain ${chainId} is not an EVM chain`);
    // Use override RPC if configured, otherwise use the default chain RPC
    const rpcOverride = chainRpcOverrides[chainId];
    const chain = rpcOverride ? getCustomEvmChain(EVM_CHAINS[chainId].viem, rpcOverride) : EVM_CHAINS[chainId].viem;
    return createPublicClient({
        chain,
        transport: http(),
        pollingInterval: EVM_CHAINS[chainId].pollingInterval,
    });
}
export function chainIdToWalletClient(chainId, account) {
    invariant(EVM_CHAINS[chainId], `Chain ${chainId} is not an EVM chain`);
    // Use override RPC if configured, otherwise use the default chain RPC
    const rpcOverride = chainRpcOverrides[chainId];
    const chain = rpcOverride ? getCustomEvmChain(EVM_CHAINS[chainId].viem, rpcOverride) : EVM_CHAINS[chainId].viem;
    return createWalletClient({
        chain,
        transport: http(),
        account,
        pollingInterval: EVM_CHAINS[chainId].pollingInterval,
    });
}
export function getNativeRequired(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} is not supported`);
    return ALL_CHAINS[chainId].nativeRequired;
}
export function canDepositNative(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} is not supported`);
    return ALL_CHAINS[chainId].canDepositNative;
}
export function isMainnet(chainId) {
    return (EVM_MAINNET[chainId] !== undefined || RELAY_SOLANA_MAINNET_CHAIN_ID === chainId || HYPERLIQUID_CHAIN_ID === chainId);
}
export function isTestnet(chainId) {
    return EVM_TESTNET[chainId] !== undefined;
}
export function getDefaultToken(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} is not supported`);
    return ALL_CHAINS[chainId].defaultToken;
}
export function getChainName(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} is not supported`);
    const chain = ALL_CHAINS[chainId];
    if (isEvmChain(chainId)) {
        return chain.viem.name;
    }
    return chain.name;
}
export function getCoingeckoName(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} is not supported`);
    return ALL_CHAINS[chainId].coingeckoName;
}
export function getPaymentUrl(address, amount, currency, chainId, decimals) {
    // Get chain type to determine URL format
    const chainType = getChainType(chainId);
    const chain = ALL_CHAINS[chainId];
    invariant(chain, `Chain ${chainId} is not supported`);
    switch (chainType) {
        case ChainType.EVM: {
            // For EVM chains, follow EIP-681 format
            // Format: ethereum:[address]@[chainId]?value=[amount]&symbol=[symbol]
            const params = new URLSearchParams();
            // Add value for native token transfers (skip if amount not provided, e.g. deposit_first)
            if (currency === chain.nativeToken.symbol && amount !== undefined) {
                params.append("value", amount.toString());
            }
            // Handle token transfers differently from native transfers
            if (currency !== chain.nativeToken.symbol) {
                // For ERC20 tokens, the issue is that mobile wallets often ignore chainId
                // and default to mainnet when they see ethereum: scheme
                // Let's try a different approach: put chainId first and be very explicit
                const tokenParams = new URLSearchParams();
                // Put chainId first to make it more prominent
                if (chainId !== mainnet.id) {
                    tokenParams.append("chainId", chainId.toString());
                }
                // For ERC20 tokens, convert from smallest unit to display units using decimals
                // For example: 2400623 (raw) with 6 decimals becomes "2.400623"
                // Skip amount if not provided (e.g. deposit_first orders)
                if (amount !== undefined) {
                    let displayAmount;
                    if (decimals !== undefined && currency !== chain.nativeToken.symbol) {
                        // Convert from smallest unit to display unit for ERC20 tokens
                        const divisor = BigInt(10 ** decimals);
                        const wholePart = amount / divisor;
                        const fractionalPart = amount % divisor;
                        if (fractionalPart === BigInt(0)) {
                            displayAmount = wholePart.toString();
                        }
                        else {
                            // Format fractional part with leading zeros if needed
                            const fractionalStr = fractionalPart.toString().padStart(decimals, "0");
                            // Remove trailing zeros
                            const trimmedFractional = fractionalStr.replace(/0+$/, "");
                            displayAmount = trimmedFractional ? `${wholePart}.${trimmedFractional}` : wholePart.toString();
                        }
                    }
                    else {
                        // For native tokens or when decimals not provided, use raw amount
                        displayAmount = amount.toString();
                    }
                    tokenParams.append("amount", displayAmount);
                }
                tokenParams.append("address", address); // recipient address
                // For Arbitrum and other L2s, try a more explicit format
                if (chainId !== mainnet.id) {
                    // Include the token contract address in the path more explicitly
                    const url = `ethereum:${currency}@${chainId}?${tokenParams.toString()}`;
                    return url;
                }
                else {
                    // Mainnet tokens
                    const url = `ethereum:${currency}?${tokenParams.toString()}`;
                    return url;
                }
            }
            // For native ETH transfers:
            if (chainId !== mainnet.id) {
                // For non-mainnet chains, use the same explicit format as tokens
                // to make sure wallets recognize the correct chain
                const nativeParams = new URLSearchParams();
                nativeParams.append("chainId", chainId.toString());
                if (amount !== undefined) {
                    nativeParams.append("value", amount.toString());
                }
                const url = `ethereum:${address}@${chainId}?${nativeParams.toString()}`;
                return url;
            }
            else {
                // For mainnet, use the simple format
                const queryString = params.toString();
                const url = `ethereum:${address}${queryString ? `?${queryString}` : ""}`;
                return url;
            }
        }
        case ChainType.SOLANA: {
            // Solana URL format for Phantom and other mobile wallets
            const params = new URLSearchParams();
            // Check if this is native SOL or SPL token
            // The address "11111111111111111111111111111111" is Solana's System Program, indicating native SOL
            const isNativeSOL = currency === chain.nativeToken.symbol || currency === "SOL" || currency === "11111111111111111111111111111111";
            if (isNativeSOL) {
                // Native SOL transfers - convert from lamports to SOL
                if (amount !== undefined) {
                    let displayAmount;
                    if (decimals !== undefined) {
                        const divisor = BigInt(10 ** decimals);
                        const wholePart = amount / divisor;
                        const fractionalPart = amount % divisor;
                        if (fractionalPart === BigInt(0)) {
                            displayAmount = wholePart.toString();
                        }
                        else {
                            const fractionalStr = fractionalPart.toString().padStart(decimals, "0");
                            const trimmedFractional = fractionalStr.replace(/0+$/, "");
                            displayAmount = trimmedFractional ? `${wholePart}.${trimmedFractional}` : wholePart.toString();
                        }
                    }
                    else {
                        // Fallback: assume SOL has 9 decimals
                        const divisor = BigInt(1000000000); // 1e9
                        const wholePart = amount / divisor;
                        const fractionalPart = amount % divisor;
                        if (fractionalPart === BigInt(0)) {
                            displayAmount = wholePart.toString();
                        }
                        else {
                            const fractionalStr = fractionalPart.toString().padStart(9, "0");
                            const trimmedFractional = fractionalStr.replace(/0+$/, "");
                            displayAmount = trimmedFractional ? `${wholePart}.${trimmedFractional}` : wholePart.toString();
                        }
                    }
                    // For native SOL, use simple format without spl-token parameter
                    params.append("amount", displayAmount);
                }
            }
            else {
                // SPL token transfers
                if (amount !== undefined) {
                    let displayAmount;
                    if (decimals !== undefined) {
                        const divisor = BigInt(10 ** decimals);
                        const wholePart = amount / divisor;
                        const fractionalPart = amount % divisor;
                        if (fractionalPart === BigInt(0)) {
                            displayAmount = wholePart.toString();
                        }
                        else {
                            const fractionalStr = fractionalPart.toString().padStart(decimals, "0");
                            const trimmedFractional = fractionalStr.replace(/0+$/, "");
                            displayAmount = trimmedFractional ? `${wholePart}.${trimmedFractional}` : wholePart.toString();
                        }
                    }
                    else {
                        displayAmount = amount.toString();
                    }
                    params.append("amount", displayAmount);
                }
                params.append("spl-token", currency); // token mint address
            }
            const queryString = params.toString();
            const url = queryString ? `solana:${address}?${queryString}` : `solana:${address}`;
            console.log("Solana URL (isNativeSOL:", isNativeSOL, "):", url);
            return url;
        }
        case ChainType.HYPERLIQUID: {
            // NOTE: Hyperliquid is only supported as destination chain (not source chain).
            // Payment URLs are not needed since users cannot send FROM Hyperliquid in our flow.
            // Return address as placeholder (this code path should not be reached).
            return address;
        }
        default:
            // Fallback to just the address if chain type is unknown
            return address;
    }
}
export function getExplorerTxUrl(chainId, txHash) {
    if (chainId === b3Viem.id) {
        return "https://explorer.b3.fun/b3/tx/" + txHash;
    }
    if (EVM_CHAINS[chainId]) {
        return EVM_CHAINS[chainId].viem.blockExplorers?.default.url + "/tx/" + txHash;
    }
    if (HYPERLIQUID_CHAINS[chainId]) {
        return HYPERLIQUID_CHAINS[chainId].blockExplorer.url + "/tx/" + txHash;
    }
    // Default to Solscan for Solana transactions
    return "https://solscan.io/tx/" + txHash;
}
export function getExplorerAddressUrl(chainId, address) {
    if (EVM_CHAINS[chainId]) {
        return EVM_CHAINS[chainId].viem.blockExplorers?.default.url + "/address/" + address;
    }
    if (HYPERLIQUID_CHAINS[chainId]) {
        return HYPERLIQUID_CHAINS[chainId].blockExplorer.url + "/address/" + address;
    }
    // Default to Solscan for Solana addresses
    return "https://solscan.io/account/" + address;
}
export function getMulticall3Address(chainId) {
    const chainType = getChainType(chainId);
    invariant(chainType === ChainType.EVM, "chainType must be EVM");
    const multicall3 = EVM_CHAINS[chainId].viem.contracts?.multicall3;
    invariant(multicall3, `multicall3 of chain ${chainId} undefined`);
    return multicall3.address;
}
export function getNativeToken(chainId) {
    invariant(ALL_CHAINS[chainId], `Chain ${chainId} undefined`);
    return ALL_CHAINS[chainId].nativeToken;
}
export function isEvmChain(chainId) {
    return Boolean(EVM_CHAINS[chainId]);
}
export function isHyperliquidChain(chainId) {
    return HYPERLIQUID_CHAINS[chainId] !== undefined;
}
export function getHyperliquidChain(chainId) {
    invariant(HYPERLIQUID_CHAINS[chainId], `Chain ${chainId} is not a Hyperliquid chain`);
    return HYPERLIQUID_CHAINS[chainId];
}
/**
 * Get available chain IDs for AnySpend based on context (source or destination).
 * Filters out chains that shouldn't be available for the given context.
 *
 * @param context - "from" for source chains, "to" for destination chains
 * @returns Array of available chain IDs
 *
 * @example
 * // Get source chains (excludes Hyperliquid)
 * const sourceChains = getAvailableChainIds("from") // [1, 8453, 137, ...]
 *
 * // Get destination chains (includes Hyperliquid)
 * const destChains = getAvailableChainIds("to") // [1, 8453, 137, ..., 1337]
 */
export function getAvailableChainIds(context) {
    const allChainIds = Object.values(ALL_CHAINS).map(chain => chain.id);
    if (context === "from") {
        // Hyperliquid is only supported as destination chain, not source chain
        return allChainIds.filter(chainId => chainId !== HYPERLIQUID_CHAIN_ID);
    }
    // For destination ("to"), all chains are available including Hyperliquid
    return allChainIds;
}
